import { IPublicClientApplication, AccountInfo } from "@azure/msal-browser";
import { graphScopes } from "./authConfig";

export async function acquireGraphToken(
  msal: IPublicClientApplication,
  account: AccountInfo
): Promise<string> {
  try {
    const res = await msal.acquireTokenSilent({ account, scopes: graphScopes });
    return res.accessToken;
  } catch {
    const res = await msal.acquireTokenPopup({ scopes: graphScopes });
    return res.accessToken;
  }
}

export type Team = {
  id: string;
  displayName: string;
  description?: string;
  createdDateTime?: string;
  lastActivityDateTime?: string;
};

type BatchRequest = { id: string; method: "GET"; url: string };
type BatchResponse<T> = { id: string; status: number; body?: T };

async function graphBatch<T>(
  token: string,
  requests: BatchRequest[]
): Promise<BatchResponse<T>[]> {
  const out: BatchResponse<T>[] = [];
  for (let i = 0; i < requests.length; i += 20) {
    const chunk = requests.slice(i, i + 20);
    const res = await fetch("https://graph.microsoft.com/v1.0/$batch", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ requests: chunk })
    });
    if (!res.ok) {
      console.error("graph $batch failed", res.status, await res.text());
      continue;
    }
    const data = (await res.json()) as { responses: BatchResponse<T>[] };
    out.push(...data.responses);
  }
  return out;
}

export async function fetchTeamCreatedDates(
  token: string,
  teamIds: string[]
): Promise<Map<string, string>> {
  const requests: BatchRequest[] = teamIds.map((id) => ({
    id,
    method: "GET",
    url: `/groups/${id}?$select=id,createdDateTime`
  }));
  const responses = await graphBatch<{ id: string; createdDateTime?: string }>(
    token,
    requests
  );
  const map = new Map<string, string>();
  for (const r of responses) {
    if (r.status === 200 && r.body?.createdDateTime) {
      map.set(r.id, r.body.createdDateTime);
    }
  }
  return map;
}

export async function fetchTeamLastActivity(
  token: string,
  teamIds: string[]
): Promise<Map<string, string>> {
  const requests: BatchRequest[] = teamIds.map((id) => ({
    id,
    method: "GET",
    url: `/teams/${id}/primaryChannel/messages?$top=1&$select=createdDateTime,lastModifiedDateTime`
  }));
  const responses = await graphBatch<{
    value?: Array<{ createdDateTime?: string; lastModifiedDateTime?: string }>;
  }>(token, requests);
  const map = new Map<string, string>();
  for (const r of responses) {
    if (r.status === 200 && r.body?.value?.[0]) {
      const m = r.body.value[0];
      const ts = m.lastModifiedDateTime ?? m.createdDateTime;
      if (ts) map.set(r.id, ts);
    }
  }
  return map;
}

export async function fetchJoinedTeams(token: string): Promise<Team[]> {
  const res = await fetch("https://graph.microsoft.com/v1.0/me/joinedTeams", {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error(`Graph /me/joinedTeams failed: ${res.status}`);
  const data = await res.json();
  return data.value as Team[];
}

export async function fetchUserGroupIds(token: string): Promise<string[]> {
  const res = await fetch("https://graph.microsoft.com/v1.0/me/memberOf?$select=id", {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) return [];
  const data = await res.json();
  return (data.value as Array<{ id: string }>).map((g) => g.id);
}

export async function isMemberOfGroup(
  token: string,
  groupOid: string
): Promise<boolean> {
  const target = groupOid.trim().toLowerCase();
  if (!target) return true;
  const res = await fetch(
    "https://graph.microsoft.com/v1.0/me/checkMemberObjects",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ ids: [target] })
    }
  );
  if (!res.ok) {
    console.error("checkMemberObjects failed", res.status, await res.text());
    return false;
  }
  const data = (await res.json()) as { value: string[] };
  return data.value.map((v) => v.toLowerCase()).includes(target);
}
